import { useEffect, useMemo, useRef, useState } from "react";

import { UseQueryResult } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useLocation, useMatch } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { NO_FOLDER_FILTER } from "src/api";
import {
  FlowFolderListItem,
  FlowFolderListResponse,
} from "src/clients/flow-api";
import { toastActions } from "src/design-system/Toast/utils";
import { AddFolder } from "src/flowsOverview/v2/AddFolder";
import { Folder } from "src/flowsOverview/v2/Folder";
import { FolderListSkeleton } from "src/flowsOverview/v2/FolderListSkeleton";
import { DeleteFolderModal } from "src/flowsOverview/v2/folderModals/DeleteFolderModal";
import { EditFolderModal } from "src/flowsOverview/v2/folderModals/EditFolderModal";
import {
  useCreateFolder,
  useDeleteFolder,
  useEditFolder,
} from "src/flowsOverview/v2/folderQueries";
import { useLatestViewedFolder } from "src/flowsOverview/v2/useLatestViewedFolder";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import {
  getBaseUrl,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";

type Props = {
  organizationId: string;
  workspaceId: string;
  folderResult: UseQueryResult<FlowFolderListResponse, Error>;
  displayAsNested?: boolean;
};

export const FolderList: React.FC<Props> = ({
  organizationId,
  workspaceId,
  folderResult,
  displayAsNested = false,
}) => {
  const [selectedFolderParam, setSelectedFolderParam] =
    useFolderIdSearchParam();
  const sortedFolders = useMemo(
    () =>
      folderResult.data?.folders.toSorted((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
      ) ?? ([] as FlowFolderListItem[]),
    [folderResult.data?.folders],
  );
  const { flowFolders } = useCapabilities();
  const createFolderMutations = useCreateFolder();
  const editFolderMutations = useEditFolder(workspaceId);
  const deleteFolderMutations = useDeleteFolder(workspaceId);
  const userId = useCurrentUserId();

  const [latestViewedFolder, setLatestViewedFolder] = useLatestViewedFolder({
    userId: userId!,
    workspaceId,
  });

  const [createFolderModalOpen, setCreateFolderModalOpen] =
    useState<boolean>(false);

  const [folderToEdit, setFolderToEdit] = useState<FlowFolderListItem>();
  const [editFolderModalOpen, setEditFolderModalOpen] =
    useState<boolean>(false);

  const [folderToDelete, setFolderToDelete] = useState<FlowFolderListItem>();
  const [deleteFolderModalOpen, setDeleteFolderModalOpen] =
    useState<boolean>(false);

  const onRequestDeleteFolder = (folder: FlowFolderListItem) => {
    setFolderToDelete(folder);
    setDeleteFolderModalOpen(true);
  };

  const onCopyLink = (folder: FlowFolderListItem | undefined) => {
    copyTextToClipboard(
      getBaseUrl() +
        getUrlToWsDashboard({
          orgId: organizationId,
          wsId: workspaceId,
          page: WsDashboardPageCode.Flows,
          folderId: folder?.id,
        }),
    ).then(() => {
      toastActions.success({
        title: "Link copied to clipboard",
      });
    });
  };

  const onRequestSelect = (folderId: string) => {
    setSelectedFolderParam(folderId);
  };

  const getFirstNonEmptyFolder = () =>
    sortedFolders.find((folder) => folder.flow_count > 0)?.id;

  const isOnFlowsPage = Boolean(
    useMatch({ path: "/org/:orgId/ws/:wsId/flows", end: false }),
  );
  // Tight match for the flows *list* page (no descendants). Used to gate the
  // URL write-back below — when this `FolderList` is rendered on a flow detail
  // page (e.g. inside the collapsed-sidebar hover popover), we must not push
  // `?folder-id=…` onto that URL: it strips any active hash (closing hash-
  // driven modals like flow Settings) and pollutes browser history.
  const isFlowsListPage = Boolean(
    useMatch({ path: "/org/:orgId/ws/:wsId/flows", end: true }),
  );

  const selectedFolderId = useMemo(() => {
    if (!selectedFolderParam && sortedFolders.length > 0) {
      if (
        latestViewedFolder &&
        sortedFolders.some((folder) => folder.id === latestViewedFolder)
      ) {
        return latestViewedFolder;
      }
      return getFirstNonEmptyFolder() ?? NO_FOLDER_FILTER;
    }
    if (selectedFolderParam) return selectedFolderParam;
    return NO_FOLDER_FILTER;
  }, [
    selectedFolderParam,
    sortedFolders,
    latestViewedFolder,
    getFirstNonEmptyFolder,
  ]);

  useEffect(() => {
    if (!folderResult.data) return;

    if (selectedFolderId !== selectedFolderParam && isFlowsListPage) {
      setSelectedFolderParam(selectedFolderId);
    }
    if (selectedFolderId !== latestViewedFolder) {
      setLatestViewedFolder(selectedFolderId);
    }
  }, [
    selectedFolderId,
    selectedFolderParam,
    latestViewedFolder,
    folderResult.data,
    isFlowsListPage,
  ]);

  const listRef = useRef<HTMLUListElement>(null);

  const navigateToSelectedFolder = (
    listRef: React.RefObject<HTMLUListElement | null>,
  ) => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector("[data-selected]");
      selectedEl?.scrollIntoView({ block: "start" });
    }
  };

  const location = useLocation();
  useEffect(() => {
    navigateToSelectedFolder(listRef);
  }, [location.pathname, sortedFolders]);

  const addFolder = (
    <AddFolder
      onRequestAddFolder={() => {
        setCreateFolderModalOpen(true);
      }}
    />
  );

  return (
    <div
      className={twJoin(
        "flex h-full flex-col gap-y-2",
        displayAsNested && "ml-3 border-gray-200 border-l pl-1",
      )}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {folderResult.isLoading && <FolderListSkeleton />}
      {!folderResult.isLoading && (
        <>
          <ul
            ref={listRef}
            className="decideScrollbarThin flex max-h-96 flex-col gap-y-2 overflow-y-scroll overscroll-auto"
          >
            {sortedFolders.map((folder: FlowFolderListItem) => (
              <Folder
                key={folder.id}
                folder={folder}
                selected={folder.id === selectedFolderId && isOnFlowsPage}
                isScrollAnchor={folder.id === selectedFolderId}
                to={getUrlToWsDashboard({
                  orgId: organizationId,
                  wsId: workspaceId,
                  page: WsDashboardPageCode.Flows,
                  folderId: folder.id,
                })}
                onCopyLink={onCopyLink}
                onRequestDelete={onRequestDeleteFolder}
                onRequestEdit={() => {
                  setFolderToEdit(folder);
                  setEditFolderModalOpen(true);
                }}
                onRequestSelect={onRequestSelect}
              />
            ))}
            <Folder
              count={folderResult.data?.flows_outside_folders_count ?? 0}
              folder={undefined}
              selected={selectedFolderId === NO_FOLDER_FILTER && isOnFlowsPage}
              isScrollAnchor={selectedFolderId === NO_FOLDER_FILTER}
              to={getUrlToWsDashboard({
                orgId: organizationId,
                wsId: workspaceId,
                page: WsDashboardPageCode.Flows,
                folderId: NO_FOLDER_FILTER,
              })}
              onCopyLink={onCopyLink}
              onRequestDelete={onRequestDeleteFolder}
              onRequestSelect={() => {
                onRequestSelect(NO_FOLDER_FILTER);
              }}
            />
          </ul>
        </>
      )}
      {flowFolders.canCreate && addFolder}
      <div onClick={(e) => e.stopPropagation()}>
        <EditFolderModal
          mode="create"
          open={createFolderModalOpen}
          onClose={() => setCreateFolderModalOpen(false)}
          onConfirm={async (name: string) => {
            const response = await createFolderMutations.mutateAsync({
              name,
              workspace_id: workspaceId,
            });
            setSelectedFolderParam(response.data.id);
            setLatestViewedFolder(response.data.id);
          }}
        />
        <EditFolderModal
          afterLeave={() => {
            setFolderToEdit(undefined);
          }}
          folder={folderToEdit}
          mode="edit"
          open={editFolderModalOpen}
          onClose={() => setEditFolderModalOpen(false)}
          onConfirm={async (name: string) => {
            if (folderToEdit) {
              await editFolderMutations.mutateAsync({
                folderId: folderToEdit.id,
                flowFolderUpdate: { name },
              });
              navigateToSelectedFolder(listRef);
            }
          }}
        />
        <DeleteFolderModal
          afterLeave={() => {
            setFolderToDelete(undefined);
          }}
          folder={folderToDelete}
          isOpen={deleteFolderModalOpen}
          onClose={() => {
            setDeleteFolderModalOpen(false);
          }}
          onConfirm={wrapWithAxiosResponseErrorHandler(async () => {
            if (!folderToDelete) return;

            try {
              await deleteFolderMutations.mutateAsync(folderToDelete.id);
            } catch (error) {
              // reachable when the folder gained a flow since the list was loaded,
              // or holds one this user cannot see
              if (isAxiosError(error) && error.response?.status === 409) {
                toastActions.failure({
                  title: "Folder is not empty",
                  description:
                    "Move or delete the Decision Flows inside it before deleting the folder.",
                });
                return;
              }
              throw error;
            }
            setSelectedFolderParam(NO_FOLDER_FILTER);
            setDeleteFolderModalOpen(false);
          })}
        />
      </div>
    </div>
  );
};
